import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Trash2,
  Edit2,
  ChevronDown,
  TrendingDown,
  Pencil,
  Briefcase,
  X,
  Save,
  Paperclip,
  FileText,
  Loader2,
  CheckCircle2,
  Check,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import api, { uploadFileUrl } from "@/lib/api";
import Modal from "./Modal";
import { fmtShekel, todayISO, dateInPeriod, taxIncluded, MONTHS_HE } from "@/lib/utils";
import { useSettings } from "@/contexts/SettingsContext";

const SECTIONS = {
  fixed: {
    label: "קבועות",
    color: "#F59E0B",
    bg: "#FEF3C7",
    text: "#92400E",
    bgSoft: "#FFFBEB",
  },
  variable: {
    label: "משתנות",
    color: "#0EA5E9",
    bg: "#E0F2FE",
    text: "#075985",
    bgSoft: "#F0F9FF",
  },
  business: {
    label: "הכנסות מוכרות במס",
    color: "#8B5CF6",
    bg: "#F3E8FF",
    text: "#6B21A8",
    bgSoft: "#FAF5FF",
  },
};

export default function ExpensesCard({
  month,
  year,
  refreshKey,
  onChange,
  startOpen = true,
}) {
  const [open, setOpen] = useState(startOpen);
  const { rates, taxRates, save: saveTax } = useSettings();
  const [fixed, setFixed] = useState([]);
  const [variable, setVariable] = useState([]);
  const [businessItems, setBusinessItems] = useState([]);
  const [businessTemplates, setBusinessTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [varCats, setVarCats] = useState([]);
  const [addMode, setAddMode] = useState(null); // "fixed" | "variable" | "business"
  const [editing, setEditing] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [form, setForm] = useState({
    name: "",
    amount: "",
    date: todayISO(),
    note: "",
  });

  const load = async () => {
    const [f, v, t, vc, biz, bizTpl] = await Promise.all([
      api.get(`/fixed-expenses`, { params: { month, year } }),
      api.get(`/variable-expenses`, { params: { month, year } }),
      api.get(`/config/fixed-templates`),
      api.get(`/config/variable-categories`),
      api.get(`/business-items`, { params: { month, year } }),
      api.get(`/config/business-templates`),
    ]);
    setFixed(f.data);
    setVariable(v.data);
    setTemplates(t.data);
    setVarCats(vc.data.categories || []);
    setBusinessItems(biz.data);
    setBusinessTemplates(bizTpl.data);
  };

  useEffect(() => {
    load();
  }, [month, year, refreshKey]);

  const totalFixed = fixed.reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalVariable = variable.reduce((s, x) => s + Number(x.amount || 0), 0);
  const total = totalFixed + totalVariable;

  const startAdd = (mode, prefill = {}) => {
    setAddMode(mode);
    setEditing(null);
    setForm({
      name: prefill.name || "",
      amount: prefill.amount ?? "",
      date: prefill.date || dateInPeriod(month, year),
      note: prefill.note || "",
      is_taxable: false,
    });
  };
  const startEdit = (mode, item) => {
    setAddMode(mode);
    setEditing(item);
    setForm({
      name: item.name,
      amount: item.amount,
      date: item.date,
      note: item.note || "",
      is_taxable: item.is_taxable === true,
    });
  };
  const closeForm = () => {
    setAddMode(null);
    setEditing(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const dateStr = form.date || todayISO();
      const [y, m] = dateStr.split("-").map((x) => parseInt(x, 10));
      const payload = {
        name: form.name || "ללא שם",
        amount: parseFloat(form.amount) || 0,
        date: dateStr,
        month: m,
        year: y,
        note: form.note || "",
        attachment_file: editing?.attachment_file || "",
        is_taxable: form.is_taxable === true,
        ...(addMode === "fixed" ? { is_recurring: editing?.is_recurring || false } : {}),
      };
      const url =
        addMode === "fixed"
          ? "/fixed-expenses"
          : addMode === "variable"
          ? "/variable-expenses"
          : "/business-items";
      if (editing) await api.put(`${url}/${editing.id}`, payload);
      else await api.post(url, payload);
      await load();
      onChange?.();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      closeForm();
    } catch (e) {
      alert("שגיאה בשמירה: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const del = async (mode, id) => {
    const url =
      mode === "fixed"
        ? "/fixed-expenses"
        : mode === "variable"
        ? "/variable-expenses"
        : "/business-items";
    await api.delete(`${url}/${id}`);
    await load();
    onChange?.();
  };

  const updateAttachment = async (mode, item, filename) => {
    const url =
      mode === "fixed"
        ? "/fixed-expenses"
        : mode === "variable"
        ? "/variable-expenses"
        : "/business-items";
    await api.put(`${url}/${item.id}`, {
      name: item.name,
      amount: item.amount,
      date: item.date,
      month: item.month,
      year: item.year,
      note: item.note || "",
      attachment_file: filename,
    });
    await load();
    setDetailItem((d) =>
      d && d.item.id === item.id ? { ...d, item: { ...d.item, attachment_file: filename } } : d
    );
  };

  const toggleRecurring = async (item) => {
    await api.put(`/fixed-expenses/${item.id}`, {
      name: item.name,
      amount: item.amount,
      date: item.date,
      month: item.month,
      year: item.year,
      note: item.note || "",
      attachment_file: item.attachment_file || "",
      is_taxable: item.is_taxable === true,
      is_recurring: !item.is_recurring,
    });
    await load();
    onChange?.();
  };

  const toggleTaxable = async (mode, item) => {
    const url =
      mode === "fixed"
        ? "/fixed-expenses"
        : mode === "variable"
        ? "/variable-expenses"
        : "/business-items";
    const payload = {
      name: item.name,
      amount: item.amount,
      date: item.date,
      month: item.month,
      year: item.year,
      note: item.note || "",
      attachment_file: item.attachment_file || "",
      is_taxable: item.is_taxable !== true,
    };
    if (mode === "fixed") payload.is_recurring = item.is_recurring || false;
    await api.put(`${url}/${item.id}`, payload);
    await load();
    onChange?.();
  };

  const saveTemplate = async () => {
    if (!editTemplate.name) return;
    const payload = {
      name: editTemplate.name,
      default_amount:
        editTemplate.default_amount === "" || editTemplate.default_amount == null
          ? null
          : parseFloat(editTemplate.default_amount),
      order: editTemplate.order ?? 999,
    };
    if (editTemplate.id) {
      await api.put(`/config/fixed-templates/${editTemplate.id}`, payload);
    } else {
      await api.post(`/config/fixed-templates`, payload);
    }
    setEditTemplate(null);
    load();
  };
  const deleteTemplate = async (id) => {
    await api.delete(`/config/fixed-templates/${id}`);
    load();
  };

  const addCategory = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const { data } = await api.post(`/config/variable-categories`, { name: trimmed });
    setVarCats(data.categories);
  };
  const deleteVariableCategory = async (name) => {
    if (!confirm(`למחוק את הקטגוריה "${name}"?`)) return;
    const { data } = await api.delete(`/config/variable-categories`, { params: { name } });
    setVarCats(data.categories);
  };

  const label = `${MONTHS_HE[month - 1]} ${year}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.05 }}
      className="card-surface p-6 md:p-8"
      data-testid="expenses-card"
    >
      <button
        className="w-full flex items-center justify-between gap-4"
        onClick={() => setOpen((v) => !v)}
        data-testid="expenses-toggle"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #FDECEC, #FFE4E6)",
              color: "var(--expense)",
            }}
          >
            <TrendingDown size={22} />
          </div>
          <div className="text-start">
            <div className="font-heebo font-bold text-xl text-slate-900">הוצאות</div>
            <div className="text-xs text-slate-500">{label} · קבועות + משתנות</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="num-pop text-2xl"
            style={{ color: "var(--expense)" }}
            data-testid="expenses-total"
          >
            {fmtShekel(total)}
          </div>
          <ChevronDown
            size={22}
            className="text-slate-400 transition-transform"
            style={{ transform: open ? "rotate(180deg)" : "" }}
          />
        </div>
      </button>

      {savedFlash && (
        <div
          className="fixed z-50 shadow-2xl rounded-2xl px-5 py-3 flex items-center gap-2 font-heebo font-semibold"
          style={{
            top: 24,
            insetInlineStart: "50%",
            transform: "translateX(-50%)",
            background: "#16A34A",
            color: "#fff",
            animation: "fadeIn 200ms ease",
          }}
          data-testid="saved-flash"
        >
          <CheckCircle2 size={18} /> נשמר בהצלחה
        </div>
      )}

      {open && (
        <div className="mt-8 space-y-10" data-testid="expenses-body">
          {/* Auto-contribution card (Pension + Education fund) */}
          <AutoContribCard
            month={month}
            year={year}
            fixed={fixed}
            onSeeded={() => {
              load();
              onChange?.();
            }}
          />

          {/* ================ Fixed section ================ */}
          <Section
            section={SECTIONS.fixed}
            total={totalFixed}
            actions={
              <>
                <button
                  className="btn-ghost text-xs"
                  onClick={() =>
                    setEditTemplate({
                      id: null,
                      name: "",
                      default_amount: "",
                      order: 999,
                    })
                  }
                  data-testid="add-template-btn"
                >
                  <Pencil size={14} /> ניהול קבועות
                </button>
                <button
                  className="btn-secondary text-sm"
                  onClick={() => startAdd("fixed")}
                  data-testid="add-fixed-expense-btn"
                >
                  <Plus size={14} /> הוסף
                </button>
              </>
            }
          >
            <div className="flex flex-wrap gap-2 mb-4">
              {templates.map((t, idx) => {
                const palette = [
                  ["#FEF3C7", "#92400E"],
                  ["#DBEAFE", "#1E40AF"],
                  ["#DCFCE7", "#166534"],
                  ["#FEE2E2", "#991B1B"],
                  ["#F3E8FF", "#6B21A8"],
                  ["#FCE7F3", "#9D174D"],
                  ["#E0F2FE", "#075985"],
                  ["#FEE9CC", "#9A3412"],
                ];
                const [bg, tx] = palette[idx % palette.length];
                return (
                  <button
                    key={t.id}
                    onClick={() =>
                      startAdd("fixed", { name: t.name, amount: t.default_amount ?? "" })
                    }
                    className="chip hover:opacity-90"
                    style={{ background: bg, color: tx }}
                    data-testid={`quick-fixed-${t.name}`}
                  >
                    {t.name}
                    {t.default_amount != null && (
                      <span className="opacity-70 font-normal">
                        · {fmtShekel(t.default_amount)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {fixed.length === 0 ? (
              <div className="empty-state">אין עדיין הוצאות קבועות לחודש זה</div>
            ) : (
              <>
                <ItemsTable
                  items={fixed}
                  onOpen={(item) => setDetailItem({ mode: "fixed", item })}
                  onEdit={(i) => startEdit("fixed", i)}
                  onDelete={(id) => del("fixed", id)}
                  onToggleRecurring={toggleRecurring}
                  onToggleTaxable={(item) => toggleTaxable("fixed", item)}
                  showRecurring={true}
                  testid="fixed-list"
                  rates={rates}
                />
              </>
            )}
          </Section>

          {/* ============ Divider ============ */}
          <SectionDivider />

          {/* ================ Variable section ================ */}
          <Section
            section={SECTIONS.variable}
            total={totalVariable}
            actions={
              <button
                className="btn-secondary text-sm"
                onClick={() => startAdd("variable")}
                data-testid="add-variable-expense-btn"
              >
                <Plus size={14} /> הוסף
              </button>
            }
          >
            {variable.length === 0 ? (
              <div className="empty-state">אין עדיין הוצאות משתנות לחודש זה</div>
            ) : (
              <>
                <ItemsTable
                  items={variable}
                  onOpen={(item) => setDetailItem({ mode: "variable", item })}
                  onEdit={(i) => startEdit("variable", i)}
                  onDelete={(id) => del("variable", id)}
                  onToggleTaxable={(item) => toggleTaxable("variable", item)}
                  rates={rates}
                  testid="variable-list"
                />
              </>
            )}
          </Section>

          {/* ================ Grand Total ================ */}
          <div
            className="rounded-2xl p-5 border-2"
            style={{
              background:
                "linear-gradient(135deg, #FDF3F3 0%, #FDECEC 60%, #F3E8FF 100%)",
              borderColor: "#F5D0D0",
            }}
          >
            <div className="flex justify-between items-center flex-wrap gap-2">
              <span className="font-heebo font-bold text-slate-800 text-lg">
                סה״כ הוצאות
              </span>
              <div className="text-end">
                <div className="text-xs text-slate-500 font-heebo">
                  קבועות {fmtShekel(totalFixed)} · משתנות {fmtShekel(totalVariable)}
                </div>
                <div
                  className="num-pop text-3xl"
                  style={{ color: "var(--expense)" }}
                  data-testid="expenses-grand-total"
                >
                  {fmtShekel(total)}
                </div>
              </div>
            </div>

            {/* Aggregated tax-recognized purple panel — all V-marked expenses (fixed + variable) */}
            <SectionTaxSummary
              items={[...fixed, ...variable]}
              testid="expenses-tax-summary-all"
              rates={rates}
              taxRates={taxRates}
            />

            {/* Tax-adjusted summary — full credit (VAT + IT + BL) */}
            {(() => {
              const allTaxable = [...fixed, ...variable].filter((x) => x.is_taxable === true);
              const taxableBase = allTaxable.reduce((s, x) => s + Number(x.amount || 0), 0);
              const vatCredit = taxIncluded(taxableBase, rates.vat);
              const itaxCredit = taxIncluded(taxableBase, rates.income_tax);
              const blCredit = taxIncluded(taxableBase, rates.social_security);
              const totalCredit = vatCredit + itaxCredit + blCredit;
              const netAfter = total - totalCredit;
              if (allTaxable.length === 0) return null;
              return (
                <div
                  className="mt-4 rounded-xl bg-white/70 p-4 border"
                  style={{ borderColor: "#F5D0D0" }}
                  data-testid="expenses-tax-adjusted-summary"
                >
                  <div className="text-xs font-heebo text-slate-500 mb-2">
                    ⬇ אחרי זיכוי מלא של המסים ({allTaxable.length} פריטים מסומנים)
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-heebo text-slate-700">
                      מע״מ ({taxRates.vat}%)
                    </span>
                    <span className="num-pop" style={{ color: "#5B21B6" }} data-testid="expenses-vat-credit">
                      −{fmtShekel(vatCredit)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-heebo text-slate-700">
                      מס הכנסה ({taxRates.income_tax}%)
                    </span>
                    <span className="num-pop" style={{ color: "#9D174D" }} data-testid="expenses-income-tax-credit">
                      −{fmtShekel(itaxCredit)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-heebo text-slate-700">
                      ביטוח לאומי ({taxRates.social_security}%)
                    </span>
                    <span className="num-pop" style={{ color: "#991B1B" }} data-testid="expenses-bl-credit">
                      −{fmtShekel(blCredit)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-red-100">
                    <span className="font-heebo font-bold text-slate-800">
                      הוצאה נטו אחרי זיכוי מלא
                    </span>
                    <span
                      className="num-pop text-2xl"
                      style={{ color: "#B91C1C" }}
                      data-testid="expenses-net-after-tax"
                    >
                      {fmtShekel(netAfter)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

        </div>
      )}

      {/* Add/Edit modal */}
      <Modal
        open={!!addMode}
        onClose={closeForm}
        title={
          editing
            ? `עריכת ${addMode === "fixed" ? "הוצאה קבועה" : addMode === "variable" ? "הוצאה משתנה" : "הוצאת עסק"}`
            : `${addMode === "fixed" ? "הוצאה קבועה" : addMode === "variable" ? "הוצאה משתנה" : "הוצאת עסק"} חדשה`
        }
        testId="expense-form-modal"
        footer={
          <>
            <button
              className="btn-primary"
              onClick={save}
              disabled={saving}
              data-testid="expense-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> שומר…
                </>
              ) : (
                "שמור"
              )}
            </button>
            <button className="btn-secondary" onClick={closeForm} disabled={saving}>
              ביטול
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">שם ההוצאה</div>
            {addMode === "fixed" ? (
              <input
                list="fixed-names"
                className="input-field"
                value={form.name}
                onChange={(e) => {
                  const v = e.target.value;
                  const t = templates.find((x) => x.name === v);
                  setForm((f) => ({
                    ...f,
                    name: v,
                    amount: t && t.default_amount != null ? t.default_amount : f.amount,
                  }));
                }}
                data-testid="expense-name-input"
              />
            ) : addMode === "business" ? (
              <input
                list="business-names"
                className="input-field"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder='למשל: מע"מ, ביטוח לאומי, אחר…'
                data-testid="expense-name-input"
              />
            ) : (
              <VariableNameSelect
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                categories={varCats}
                onAddCategory={addCategory}
                onDeleteCategory={deleteVariableCategory}
              />
            )}
            <datalist id="fixed-names">
              {templates.map((t) => (
                <option key={t.id} value={t.name} />
              ))}
            </datalist>
            <datalist id="business-names">
              {businessTemplates.map((t) => (
                <option key={t.id} value={t.name} />
              ))}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-slate-500 mb-1 font-heebo">סכום (₪)</div>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                data-testid="expense-amount-input"
              />
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1 font-heebo">תאריך</div>
              <input
                type="date"
                className="input-field"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                data-testid="expense-date-input"
              />
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">הערות (אופציונלי)</div>
            <input
              className="input-field"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              data-testid="expense-note-input"
            />
          </div>

          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <input
              type="checkbox"
              id="expense-taxable"
              checked={form.is_taxable === true}
              onChange={(e) => setForm((f) => ({ ...f, is_taxable: e.target.checked }))}
              className="w-4 h-4"
              data-testid="expense-taxable-toggle"
            />
            <label htmlFor="expense-taxable" className="text-sm font-heebo cursor-pointer flex-1">
              <span className="font-semibold text-amber-900">הוצאה חייבת במס</span>
              <span className="text-amber-700 text-xs block mt-0.5">
                יש חשבונית מס · מקטין את הבסיס לחישוב המס
              </span>
            </label>
          </div>
        </div>
      </Modal>

      {/* Manage template modal */}
      <Modal
        open={!!editTemplate}
        onClose={() => setEditTemplate(null)}
        title="ניהול הוצאות קבועות"
        testId="template-modal"
        footer={
          <>
            <button className="btn-primary" onClick={saveTemplate} data-testid="save-template-btn">
              שמור
            </button>
            <button className="btn-secondary" onClick={() => setEditTemplate(null)}>
              סגור
            </button>
          </>
        }
      >
        <div className="space-y-3 mb-4">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-2 p-3 rounded-xl bg-slate-50">
              <div className="flex-1">
                <div className="font-heebo text-slate-800">{t.name}</div>
                <div className="text-xs text-slate-500">
                  ברירת מחדל: {t.default_amount != null ? fmtShekel(t.default_amount) : "—"}
                </div>
              </div>
              <button
                className="btn-ghost"
                onClick={() =>
                  setEditTemplate({
                    id: t.id,
                    name: t.name,
                    default_amount: t.default_amount ?? "",
                    order: t.order,
                  })
                }
                data-testid={`edit-template-${t.name}`}
              >
                <Edit2 size={14} /> ערוך
              </button>
              <button
                className="btn-ghost"
                onClick={() => deleteTemplate(t.id)}
                data-testid={`delete-template-${t.name}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="border-t pt-4 space-y-3">
          <div className="font-heebo font-semibold text-slate-800">
            {editTemplate?.id ? "עריכת קבועה" : "הוספת קבועה חדשה"}
          </div>
          <input
            className="input-field"
            placeholder="שם הוצאה קבועה"
            value={editTemplate?.name || ""}
            onChange={(e) => setEditTemplate((v) => ({ ...v, name: e.target.value }))}
            data-testid="template-name-input"
          />
          <input
            type="number"
            className="input-field"
            placeholder="סכום ברירת מחדל (אופציונלי)"
            value={editTemplate?.default_amount ?? ""}
            onChange={(e) => setEditTemplate((v) => ({ ...v, default_amount: e.target.value }))}
            data-testid="template-amount-input"
          />
        </div>
      </Modal>

      {/* Detail modal (row click) */}
      <ExpenseDetailModal
        state={detailItem}
        onClose={() => setDetailItem(null)}
        onEdit={(mode, item) => {
          setDetailItem(null);
          startEdit(mode, item);
        }}
        onDelete={async (mode, id) => {
          await del(mode, id);
          setDetailItem(null);
        }}
        onUpload={updateAttachment}
      />
    </motion.div>
  );
}

/* ============== Sub-components ============== */

function Section({ section, total, subtitle, icon, actions, children }) {
  return (
    <div
      className="rounded-2xl border-2 p-5"
      style={{ borderColor: section.color + "55", background: section.bgSoft }}
      data-testid={`section-${section.label}`}
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {icon && (
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: section.bg, color: section.text }}
            >
              {icon}
            </div>
          )}
          <span
            className="chip text-sm px-3 py-1.5 font-bold"
            style={{ background: section.bg, color: section.text }}
          >
            {section.label}
          </span>
          {subtitle && (
            <span className="text-xs text-slate-500 font-heebo">· {subtitle}</span>
          )}
          <span className="text-xs text-slate-500 font-heebo">
            · סה״כ{" "}
            <span className="num-pop text-slate-800 text-sm">{fmtShekel(total)}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
      {children}
    </div>
  );
}

function SectionDivider() {
  return (
    <div className="flex items-center gap-3 py-2" aria-hidden>
      <div
        className="flex-1 h-[2px] rounded-full"
        style={{
          background:
            "linear-gradient(to left, transparent 0%, #C9C5B0 30%, #C9C5B0 70%, transparent 100%)",
        }}
      />
      <div className="w-2 h-2 rounded-full bg-slate-300" />
      <div
        className="flex-1 h-[2px] rounded-full"
        style={{
          background:
            "linear-gradient(to left, transparent 0%, #C9C5B0 30%, #C9C5B0 70%, transparent 100%)",
        }}
      />
    </div>
  );
}

function AutoContribCard({ month, year, fixed, onSeeded }) {
  const [defaults, setDefaults] = useState({ pension_amount: 1000, education_fund_amount: 200 });
  const [pension, setPension] = useState(1000);
  const [edu, setEdu] = useState(200);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/settings/auto-contrib");
        setDefaults(res.data);
        setPension(res.data.pension_amount);
        setEdu(res.data.education_fund_amount);
      } catch { /* noop */ }
    })();
  }, []);

  const existingPension = fixed.find((x) => x.name === "פנסיה");
  const existingEdu = fixed.find((x) => x.name === "קרן השתלמות");
  const bothExist = !!existingPension && !!existingEdu;

  const saveDefaults = async () => {
    await api.put("/settings/auto-contrib", {
      pension_amount: Number(pension) || 0,
      education_fund_amount: Number(edu) || 0,
      enabled: true,
    });
    setDefaults({ pension_amount: Number(pension), education_fund_amount: Number(edu) });
    setEditing(false);
  };

  const seedNow = async () => {
    setSaving(true);
    try {
      await api.post("/settings/auto-contrib/seed", null, { params: { month, year } });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      onSeeded?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-2xl border-2 p-5"
      style={{
        background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
        borderColor: "#93C5FD",
      }}
      data-testid="auto-contrib-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="font-heebo font-bold text-blue-900 text-lg">
            חסכונות והפרשות חודשיות
          </div>
          <div className="text-[11px] text-blue-800 font-heebo mt-0.5">
            פנסיה + קרן השתלמות — נוצרים כהוצאה קבועה + מסתנכרנים לחסכונות
          </div>
        </div>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setEditing((v) => !v)}
          data-testid="auto-contrib-toggle-edit"
        >
          <Pencil size={12} /> {editing ? "בטל" : "ערוך סכומים"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl bg-white/70 p-3 border border-blue-200">
          <div className="text-xs font-heebo text-blue-800 font-semibold mb-1">פנסיה</div>
          {editing ? (
            <input
              type="number"
              min="0"
              step="1"
              value={pension}
              onChange={(e) => setPension(e.target.value)}
              className="w-full bg-white rounded-lg px-3 py-2 text-lg num-pop border border-blue-300"
              data-testid="auto-contrib-pension-input"
            />
          ) : (
            <div className="num-pop text-2xl text-blue-900" data-testid="auto-contrib-pension-amount">
              {fmtShekel(defaults.pension_amount)}
            </div>
          )}
          {existingPension && !editing && (
            <div className="text-[10px] text-emerald-700 font-heebo mt-1">
              ✓ נוסף לחודש זה ({fmtShekel(existingPension.amount)})
            </div>
          )}
        </div>
        <div className="rounded-xl bg-white/70 p-3 border border-blue-200">
          <div className="text-xs font-heebo text-blue-800 font-semibold mb-1">קרן השתלמות</div>
          {editing ? (
            <input
              type="number"
              min="0"
              step="1"
              value={edu}
              onChange={(e) => setEdu(e.target.value)}
              className="w-full bg-white rounded-lg px-3 py-2 text-lg num-pop border border-blue-300"
              data-testid="auto-contrib-edu-input"
            />
          ) : (
            <div className="num-pop text-2xl text-blue-900" data-testid="auto-contrib-edu-amount">
              {fmtShekel(defaults.education_fund_amount)}
            </div>
          )}
          {existingEdu && !editing && (
            <div className="text-[10px] text-emerald-700 font-heebo mt-1">
              ✓ נוסף לחודש זה ({fmtShekel(existingEdu.amount)})
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        {editing ? (
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={saveDefaults}
            data-testid="auto-contrib-save-defaults"
          >
            <CheckCircle2 size={14} /> שמור סכומי ברירת מחדל
          </button>
        ) : bothExist ? (
          <div className="text-xs font-heebo text-blue-700">
            {savedFlash ? "✓ נוסף!" : "השורות כבר קיימות בחודש הזה"}
          </div>
        ) : (
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={seedNow}
            disabled={saving}
            data-testid="auto-contrib-seed-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            הוסף לחודש זה (הוצאה + חסכון)
          </button>
        )}
      </div>
    </div>
  );
}

function MiniTaxTile({ label, value, bg, text, bold }) {
  return (
    <div
      className="rounded-lg p-2 border"
      style={{ background: bg, borderColor: text + "33" }}
    >
      <div className="text-[10px] font-heebo" style={{ color: text }}>
        {label}
      </div>
      <div
        className={`num-pop mt-0.5 ${bold ? "text-lg" : "text-base"}`}
        style={{ color: text }}
      >
        {fmtShekel(value)}
      </div>
    </div>
  );
}

function BudgetPerRemainingDays({ month, year }) {
  const { rates } = useSettings();
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/dashboard/summary", {
          params: { month, year, view: "month" },
        });
        setSummary(res.data);
      } catch { /* noop */ }
    })();
  }, [month, year, rates.vat]);

  const today = new Date();
  const isViewedMonth =
    today.getMonth() + 1 === month && today.getFullYear() === year;
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayDay = isViewedMonth ? today.getDate() : 1;

  const cumulative = summary?.cumulative_net || 0;
  if (cumulative <= 0) {
    return (
      <div
        className="mt-4 rounded-xl p-4 border-2"
        style={{ background: "#FEE2E2", borderColor: "#FCA5A5" }}
        data-testid="budget-remaining-days"
      >
        <div className="font-heebo font-bold text-red-800">ביזבוזים ליתרה</div>
        <div className="text-sm text-red-700 mt-1">
          אין יתרה חיובית לחלוקה. יש להגדיל הכנסות או להקטין הוצאות.
        </div>
      </div>
    );
  }

  const remainingDays = Math.max(1, daysInMonth - todayDay + 1);
  // "Budget per remaining day" — user has `cumulative` left and `remainingDays` to
  // spend it in. That means: allowance-per-day = cumulative / remainingDays.
  const dailyAllowance = cumulative / remainingDays;
  const availableToday = cumulative; // full remaining pool

  return (
    <div
      className="mt-4 rounded-xl p-4 border-2"
      style={{
        background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
        borderColor: "#6EE7B7",
      }}
      data-testid="budget-remaining-days"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heebo font-bold text-emerald-900">
            ביזבוזים ליתרה
          </div>
          <div className="text-[11px] text-emerald-800 font-heebo mt-0.5">
            נותרו {remainingDays} ימים · {fmtShekel(dailyAllowance)} ליום ממוצע
          </div>
        </div>
        <div className="text-end">
          <div className="text-[11px] text-emerald-800 font-heebo">יתרה זמינה לתקופה</div>
          <div className="num-pop text-2xl text-emerald-900" data-testid="budget-available-total">
            {fmtShekel(availableToday)}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 text-xs font-heebo text-emerald-800 hover:text-emerald-900 flex items-center gap-1"
        data-testid="budget-remaining-days-toggle"
      >
        {expanded ? "◤" : "◢"} תראה יום־יום שנותר בחודש ({remainingDays} ימים)
      </button>
      {expanded && (
        <div className="mt-3 rounded-lg bg-white/70 divide-y divide-emerald-100 max-h-80 overflow-auto">
          {Array.from({ length: remainingDays }).map((_, idx) => {
            const dayNum = todayDay + idx;
            const accumulated = dailyAllowance * (idx + 1);
            return (
              <div
                key={dayNum}
                className="flex items-center justify-between p-2 text-xs font-heebo"
                data-testid={`budget-day-${dayNum}`}
              >
                <span className="text-emerald-900">
                  יום {dayNum}/{daysInMonth}
                  {idx === 0 && isViewedMonth && (
                    <span className="chip text-[10px] mr-2" style={{ background: "#059669", color: "#fff" }}>
                      היום
                    </span>
                  )}
                </span>
                <span className="text-emerald-700">
                  אם לא בזבזת עד עכשיו — {fmtShekel(accumulated)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniTaxTile_unused({ label, value, bg, text, bold }) {
  return (
    <div
      className="rounded-lg p-2 border"
      style={{ background: bg, borderColor: text + "33" }}
    >
      <div className="text-[10px] font-heebo" style={{ color: text }}>
        {label}
      </div>
      <div
        className={`num-pop mt-0.5 ${bold ? "text-lg" : "text-base"}`}
        style={{ color: text }}
      >
        {fmtShekel(value)}
      </div>
    </div>
  );
}

function SectionTaxSummary({ items, testid, rates, taxRates }) {
  const marked = (items || []).filter((x) => x.is_taxable === true);
  if (marked.length === 0) return null;
  const base = marked.reduce((s, x) => s + Number(x.amount || 0), 0);
  const vat = taxIncluded(base, rates.vat);
  const itax = taxIncluded(base, rates.income_tax);
  const bl = taxIncluded(base, rates.social_security);
  const total = vat + itax + bl;
  return (
    <div
      className="rounded-2xl border-2 p-4 mt-3"
      style={{
        background: "linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)",
        borderColor: "#C4B5FD",
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="font-heebo font-bold text-purple-900 text-sm">
            סיכום מוכר במס · {marked.length} {marked.length === 1 ? "רשומה" : "רשומות"}
          </div>
          <div className="text-[11px] text-purple-800 font-heebo mt-0.5">
            מבוסס רק על פריטים שסומנו ב-✓
          </div>
        </div>
        <div className="text-end">
          <div className="text-[11px] text-purple-800 font-heebo">בסיס חייב</div>
          <div className="num-pop text-lg text-purple-900">
            {fmtShekel(base)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MiniTaxTile label={`מע״מ ${taxRates.vat}%`} value={vat} bg="#EDE9FE" text="#5B21B6" />
        <MiniTaxTile label={`מס הכנסה ${taxRates.income_tax}%`} value={itax} bg="#FCE7F3" text="#9D174D" />
        <MiniTaxTile label={`ביטוח לאומי ${taxRates.social_security}%`} value={bl} bg="#FEE2E2" text="#991B1B" />
        <MiniTaxTile label="סה״כ מסים" value={total} bg="#F5D0FE" text="#701A75" bold />
      </div>
    </div>
  );
}

function ItemsTable({ items, onOpen, onEdit, onDelete, onToggleRecurring, onToggleTaxable, showRecurring, testid, rates }) {
  const r = rates || { vat: 0.18, income_tax: 0.25, social_security: 0.05 };
  return (
    <div
      className="rounded-2xl border border-slate-200 overflow-hidden bg-white"
      data-testid={testid}
    >
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="text-start p-3 font-heebo font-medium">שם</th>
            <th className="text-start p-3 font-heebo font-medium">תאריך</th>
            <th className="text-end p-3 font-heebo font-medium">סכום</th>
            <th className="p-3 font-heebo font-medium text-center hidden md:table-cell">
              מסים <span className="text-[10px]">(מע״מ · מ.ה. · ב״ל)</span>
            </th>
            <th className="p-3 font-heebo font-medium text-center">פעולות</th>
          </tr>
        </thead>
        <tbody>
          {items.map((x) => {
            const isTaxable = x.is_taxable === true;
            const vat = isTaxable ? taxIncluded(x.amount, r.vat) : 0;
            const itax = isTaxable ? taxIncluded(x.amount, r.income_tax) : 0;
            const bl = isTaxable ? taxIncluded(x.amount, r.social_security) : 0;
            const totalTax = vat + itax + bl;
            return (
              <tr
                key={x.id}
                className="border-t border-slate-100 hover:bg-emerald-50/40 transition"
                data-testid={`row-${x.id}`}
              >
                <td
                  className="p-3 text-slate-800 font-heebo cursor-pointer"
                  onClick={() => onOpen(x)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {x.name}
                    {x.attachment_file && (
                      <Paperclip size={12} className="text-emerald-600" />
                    )}
                    {x.is_recurring && (
                      <span
                        className="chip text-[10px] px-2 py-0.5"
                        style={{ background: "#DCFCE7", color: "#166534" }}
                      >
                        ↻ קבוע
                      </span>
                    )}
                  </div>
                  {x.note && (
                    <div className="text-xs text-slate-400 mt-0.5">{x.note}</div>
                  )}
                </td>
                <td
                  className="p-3 text-slate-500 cursor-pointer"
                  onClick={() => onOpen(x)}
                >
                  {x.date}
                </td>
                <td
                  className="p-3 text-end num-pop text-slate-800 cursor-pointer"
                  onClick={() => onOpen(x)}
                >
                  {fmtShekel(x.amount)}
                </td>
                <td className="p-2 text-center hidden md:table-cell">
                  {isTaxable ? (
                    <div className="inline-flex flex-col items-center gap-0.5">
                      <div className="text-[10px] text-slate-500 font-heebo">
                        {fmtShekel(vat)} · {fmtShekel(itax)} · {fmtShekel(bl)}
                      </div>
                      <div className="num-pop text-amber-700 text-sm">
                        זיכוי {fmtShekel(totalTax)}
                      </div>
                      <div className="num-pop text-red-700 text-sm font-bold" data-testid={`expense-net-${x.id}`}>
                        נטו {fmtShekel(x.amount - totalTax)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">לא חייב</span>
                  )}
                </td>
                <td className="p-2 whitespace-nowrap text-center">
                  <div className="flex items-center gap-1 justify-center">
                    <button
                      type="button"
                      className={`p-2 rounded-lg transition ${
                        isTaxable
                          ? "text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                          : "text-slate-300 hover:bg-slate-100 hover:text-slate-500 border-2 border-dashed border-slate-300"
                      }`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await onToggleTaxable(x);
                        } catch (err) {
                          alert("שגיאה: " + (err?.message || err));
                        }
                      }}
                      data-testid={`toggle-taxable-${x.id}`}
                      title={
                        isTaxable
                          ? "מוכר במס - לחץ כדי להסיר מחישוב"
                          : "לחץ כדי לסמן כמוכר במס"
                      }
                    >
                      <Check size={16} />
                    </button>
                    {showRecurring && (
                      <button
                        type="button"
                        className={`p-2 rounded-lg transition ${
                          x.is_recurring
                            ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                            : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        }`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await onToggleRecurring(x);
                          } catch (err) {
                            alert("שגיאה: " + (err?.message || err));
                          }
                        }}
                        data-testid={`toggle-recurring-${x.id}`}
                        title={
                          x.is_recurring
                            ? "בטל הקבעה - לא יופיע בחודשים הבאים"
                            : "הפוך לקבוע - יופיע כל חודש אוטומטית"
                        }
                      >
                        <RefreshCw size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(x);
                      }}
                      data-testid={`edit-expense-${x.id}`}
                      title="ערוך"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      type="button"
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (confirm(`למחוק את "${x.name}"?`)) {
                          try {
                            await onDelete(x.id);
                          } catch (err) {
                            alert("שגיאה במחיקה: " + (err?.message || err));
                          }
                        }
                      }}
                      data-testid={`delete-expense-${x.id}`}
                      title="מחק"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExpenseDetailModal({ state, onClose, onEdit, onDelete, onUpload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);
  if (!state) return null;
  const { mode, item } = state;

  const doUpload = async (file) => {
    setUploading(true);
    setJustUploaded(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onUpload(mode, item, data.filename);
      setJustUploaded(true);
      setTimeout(() => setJustUploaded(false), 2500);
    } catch (e) {
      alert("שגיאה בהעלאת קובץ");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={!!state}
      onClose={onClose}
      title={item.name}
      testId="expense-detail-modal"
      footer={
        <>
          <button
            className="btn-secondary"
            onClick={() => onEdit(mode, item)}
            data-testid="detail-edit-btn"
          >
            <Edit2 size={14} /> ערוך
          </button>
          <button
            className="btn-ghost text-red-600 hover:bg-red-50"
            onClick={async () => {
              if (confirm(`למחוק "${item.name}"?`)) {
                await onDelete(mode, item.id);
              }
            }}
            data-testid="detail-delete-btn"
          >
            <Trash2 size={14} /> מחק
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <InfoBlock label="סכום" value={fmtShekel(item.amount)} strong />
          <InfoBlock label="תאריך" value={item.date} />
        </div>
        {item.note && <InfoBlock label="הערות" value={item.note} />}

        {/* Attachment area */}
        <div
          className="rounded-2xl p-4 border-2 border-dashed"
          style={{
            borderColor: item.attachment_file ? "#22C55E" : "#98CBB4",
            background: item.attachment_file ? "#F0FDF4" : "#F0F7F2",
          }}
        >
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Paperclip size={16} className="text-emerald-700" />
              <div className="font-heebo font-semibold text-emerald-900">
                חשבונית / קבלה
              </div>
              {uploading && (
                <span
                  className="chip"
                  style={{ background: "#FEF3C7", color: "#92400E" }}
                  data-testid="upload-spinner"
                >
                  <Loader2 size={12} className="animate-spin" /> מעלה…
                </span>
              )}
              {!uploading && item.attachment_file && (
                <span
                  className="chip"
                  style={{ background: "#DCFCE7", color: "#166534" }}
                >
                  <CheckCircle2 size={12} /> קובץ קיים
                </span>
              )}
              {justUploaded && (
                <span
                  className="chip"
                  style={{ background: "#DCFCE7", color: "#166534" }}
                >
                  ✓ הועלה
                </span>
              )}
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx"
            onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])}
            data-testid="detail-file-input"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="detail-upload-btn"
              >
                {uploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Paperclip size={14} />
                )}
                {uploading
                  ? "מעלה…"
                  : item.attachment_file
                  ? "החלף קובץ"
                  : "העלה קובץ (PDF/תמונה)"}
              </button>
              {item.attachment_file && (
                <button
                  type="button"
                  className="btn-ghost text-xs text-red-600"
                  onClick={() => onUpload(mode, item, "")}
                  data-testid="detail-remove-attachment"
                >
                  <X size={12} /> הסר
                </button>
              )}
            </div>

            {/* Prominent "Open uploaded file" button - only shows when file exists */}
            {item.attachment_file && !uploading && (
              <a
                href={uploadFileUrl(item.attachment_file)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-heebo font-semibold text-sm shadow-md hover:shadow-lg transition"
                style={{
                  background: "linear-gradient(135deg, #22C55E, #16A34A)",
                  color: "#fff",
                }}
                data-testid="detail-open-file"
              >
                <ExternalLink size={14} /> פתח קובץ שעלה
              </a>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-2">
            תומך: PDF, JPG, PNG, WEBP, HEIC, Word, Excel · עד 5MB
          </div>
        </div>
      </div>
    </Modal>
  );
}

function InfoBlock({ label, value, strong }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
      <div className="text-xs text-slate-500 font-heebo">{label}</div>
      <div
        className={`mt-1 ${strong ? "num-pop text-xl text-slate-900" : "text-slate-800"}`}
      >
        {value}
      </div>
    </div>
  );
}

function BusinessCube({ item, bg, text, onOpen }) {
  return (
    <div
      className="rounded-2xl p-4 border-2 transition hover:shadow-lg cursor-pointer"
      style={{ background: bg, borderColor: text + "44" }}
      onClick={onOpen}
      data-testid={`business-cube-${item.name}`}
    >
      <div className="text-xs font-heebo mb-2" style={{ color: text }}>
        {item.name}
        {item.attachment_file && (
          <Paperclip size={10} className="inline ms-1 text-emerald-600" />
        )}
      </div>
      <div className="num-pop text-2xl" style={{ color: text }}>
        {fmtShekel(item.amount)}
      </div>
      <div className="text-[10px] text-slate-500 mt-1 font-heebo">{item.date}</div>
    </div>
  );
}

function BusinessFieldBox({ field, value, fileName, onChange, onUpload, onRemove }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [justOk, setJustOk] = useState(false);
  return (
    <div
      className="rounded-2xl p-3 border-2"
      style={{ background: field.color + "60", borderColor: field.color }}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-heebo" style={{ color: field.text }}>
          {field.label}
        </div>
        {busy && <Loader2 size={12} className="animate-spin" style={{ color: field.text }} />}
        {!busy && fileName && (
          <CheckCircle2 size={13} style={{ color: "#16A34A" }} />
        )}
      </div>
      <input
        type="number"
        step="0.01"
        className="input-field text-lg font-semibold"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`business-${field.k}-input`}
      />
      <input
        ref={ref}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx,.xls,.xlsx"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setBusy(true);
          try {
            await onUpload(file);
            setJustOk(true);
            setTimeout(() => setJustOk(false), 2000);
          } finally {
            setBusy(false);
            if (ref.current) ref.current.value = "";
          }
        }}
        data-testid={`business-${field.k}-file-input`}
      />
      <div className="flex items-center gap-1 mt-2">
        <button
          type="button"
          className="btn-ghost text-xs flex-1 justify-center"
          onClick={() => ref.current?.click()}
          disabled={busy}
          data-testid={`business-${field.k}-upload-btn`}
          style={{ color: field.text }}
        >
          {busy ? (
            <Loader2 size={11} className="animate-spin" />
          ) : (
            <Paperclip size={11} />
          )}
          {busy ? "מעלה…" : fileName ? "החלף" : "צרף"}
        </button>
        {fileName && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={onRemove}
            data-testid={`business-${field.k}-remove-btn`}
          >
            <X size={11} />
          </button>
        )}
      </div>
      {fileName && !busy && (
        <a
          href={uploadFileUrl(fileName)}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-heebo font-semibold w-full justify-center"
          style={{
            background: "#16A34A",
            color: "#fff",
          }}
          data-testid={`business-file-view-${field.k}`}
        >
          <ExternalLink size={11} /> פתח קובץ
        </a>
      )}
      {justOk && (
        <div className="text-[10px] text-emerald-700 text-center mt-1 font-heebo">
          ✓ הועלה
        </div>
      )}
    </div>
  );
}

function VariableNameSelect({
  value,
  onChange,
  categories,
  onAddCategory,
  onDeleteCategory,
}) {
  const [custom, setCustom] = useState(false);
  const [newCat, setNewCat] = useState("");
  return (
    <div className="space-y-2">
      {!custom ? (
        <div className="flex gap-2">
          <select
            className="input-field flex-1"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid="variable-category-select"
          >
            <option value="">-- בחר קטגוריה --</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {value && onDeleteCategory && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => onDeleteCategory(value)}
              data-testid="delete-variable-category-btn"
              title="מחק קטגוריה מהרשימה"
            >
              <Trash2 size={12} />
            </button>
          )}
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setCustom(true)}
            data-testid="add-new-category-btn"
          >
            <Plus size={12} /> חדש
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            className="input-field flex-1"
            placeholder="שם קטגוריה חדשה"
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            data-testid="new-category-input"
          />
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={async () => {
              if (!newCat.trim()) return;
              await onAddCategory(newCat);
              onChange(newCat.trim());
              setNewCat("");
              setCustom(false);
            }}
            data-testid="save-new-category-btn"
          >
            שמור
          </button>
          <button type="button" className="btn-ghost" onClick={() => setCustom(false)}>
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
